/**
 * Dance Through Nine Colours - Interactive Web Component
 * Standard Shopify custom element with desktop hover-expansion, mobile touch, and autoplay support.
 */
if (!customElements.get('dance-nine-colours')) {
  class DanceNineColours extends HTMLElement {
    constructor() {
      super();
      this.onCardMouseEnter = this.onCardMouseEnter.bind(this);
      this.onCardMouseLeave = this.onCardMouseLeave.bind(this);
      this.onCardClick = this.onCardClick.bind(this);
      this.onCardKeyDown = this.onCardKeyDown.bind(this);
      this.onNavTabClick = this.onNavTabClick.bind(this);
      this.onGridScroll = this.onGridScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionMouseLeave = this.onSectionMouseLeave.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      
      this.scrollTimeout = null;
      this.autoplayTimer = null;
      this.currentIndex = 0;
      this.activeBeforeHover = 0;
      this.isHoveringCard = false;
      this.isInteracting = false;
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-dtnc-card]'));
      this.navTabs = Array.from(this.querySelectorAll('[data-dtnc-nav-tab]'));
      this.navTrack = this.querySelector('.dtnc-mobile-nav-track');
      this.grid = this.querySelector('.dtnc-accordion-grid');

      // Autoplay Settings
      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
      const durationVal = parseFloat(this.getAttribute('data-duration-value') || '5');
      const durationUnit = this.getAttribute('data-duration-unit') || 'seconds';

      if (durationUnit === 'minutes') {
        this.intervalMs = durationVal * 60 * 1000;
      } else if (durationUnit === 'hours') {
        this.intervalMs = durationVal * 3600 * 1000;
      } else {
        this.intervalMs = durationVal * 1000;
      }

      // Desktop Hover & Click event listeners
      this.cards.forEach((card) => {
        card.addEventListener('mouseenter', this.onCardMouseEnter);
        card.addEventListener('mouseleave', this.onCardMouseLeave);
        card.addEventListener('click', this.onCardClick);
        card.addEventListener('keydown', this.onCardKeyDown);
      });

      // Mobile nav tabs event listeners
      this.navTabs.forEach((tab) => {
        tab.addEventListener('click', this.onNavTabClick);
      });

      // Section level mouseleave & touch listeners
      this.addEventListener('mouseleave', this.onSectionMouseLeave);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });

      // Scroll listener for mobile horizontal carousel
      if (this.grid) {
        this.grid.addEventListener('scroll', this.onGridScroll, { passive: true });
      }

      // Shopify Editor live block selection
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Always initialize Card 1 (index 0) as active by default on page load
      this.currentIndex = 0;
      this.activeBeforeHover = 0;
      this.activateIndex(0, false);

      // Start autoplay loop if enabled
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.cards.forEach((card) => {
        card.removeEventListener('mouseenter', this.onCardMouseEnter);
        card.removeEventListener('mouseleave', this.onCardMouseLeave);
        card.removeEventListener('click', this.onCardClick);
        card.removeEventListener('keydown', this.onCardKeyDown);
      });
      this.navTabs.forEach((tab) => {
        tab.removeEventListener('click', this.onNavTabClick);
      });
      this.removeEventListener('mouseleave', this.onSectionMouseLeave);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      if (this.grid) {
        this.grid.removeEventListener('scroll', this.onGridScroll);
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.cards.length <= 1) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.isInteracting && !this.isHoveringCard) {
          const nextIndex = (this.currentIndex + 1) % this.cards.length;
          this.activateIndex(nextIndex, true);
        }
      }, this.intervalMs);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      if (this.autoplayEnabled && !this.isInteracting && !this.isHoveringCard) {
        this.startAutoplay();
      }
    }

    onCardMouseEnter(event) {
      if (window.innerWidth <= 767) return; // Desktop only

      const card = event.currentTarget;
      const index = parseInt(card.getAttribute('data-index') || '0', 10);

      if (!this.isHoveringCard) {
        this.activeBeforeHover = this.currentIndex;
      }
      this.isHoveringCard = true;
      this.stopAutoplay();
      this.activateIndex(index, false);
    }

    onCardMouseLeave(event) {
      if (window.innerWidth <= 767) return;
    }

    onSectionMouseLeave() {
      if (window.innerWidth <= 767) return;

      if (this.isHoveringCard) {
        this.isHoveringCard = false;
        // Return to previous active card before hover
        this.activateIndex(this.activeBeforeHover, false);
        this.restartAutoplay();
      }
    }

    onTouchStart() {
      this.isInteracting = true;
      this.stopAutoplay();
    }

    onTouchEnd() {
      this.isInteracting = false;
      setTimeout(() => {
        if (!this.isInteracting && !this.isHoveringCard) {
          this.startAutoplay();
        }
      }, 1500);
    }

    onCardClick(event) {
      const card = event.currentTarget;
      
      // On desktop: ignore card body click since expansion is on hover,
      // allow default link navigation if clicking the CTA button inside active card
      if (window.innerWidth > 767) {
        if (!event.target.closest('.dtnc-card-cta')) {
          event.preventDefault();
        }
        return;
      }

      // On Mobile: click/tap expands card
      if (event.target.closest('.dtnc-card-cta') && card.classList.contains('is-active')) {
        return;
      }
      const index = parseInt(card.getAttribute('data-index') || '0', 10);
      this.activateIndex(index, true);
      this.restartAutoplay();
    }

    onCardKeyDown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const card = event.currentTarget;
        const index = parseInt(card.getAttribute('data-index') || '0', 10);
        this.activateIndex(index, true);
        this.restartAutoplay();
      }
    }

    onNavTabClick(event) {
      const tab = event.currentTarget;
      const index = parseInt(tab.getAttribute('data-index') || '0', 10);
      this.activateIndex(index, true);
      this.restartAutoplay();
    }

    onGridScroll() {
      if (window.innerWidth > 767) return;

      if (this.scrollTimeout) {
        window.cancelAnimationFrame(this.scrollTimeout);
      }

      this.scrollTimeout = window.requestAnimationFrame(() => {
        const gridLeft = this.grid.scrollLeft;
        let activeIdx = 0;
        let minDiff = Infinity;

        this.cards.forEach((card, i) => {
          const cardLeft = card.offsetLeft - this.grid.offsetLeft;
          const diff = Math.abs(cardLeft - gridLeft);
          if (diff < minDiff) {
            minDiff = diff;
            activeIdx = i;
          }
        });

        if (activeIdx !== this.currentIndex) {
          this.currentIndex = activeIdx;
          this.updateActiveStates(activeIdx, false);
          this.restartAutoplay();
        }
      });
    }

    onBlockSelect(event) {
      const blockId = event.detail.blockId;
      const targetCard = this.cards.find((card) => card.getAttribute('data-block-id') === blockId);
      if (targetCard) {
        const index = parseInt(targetCard.getAttribute('data-index') || '0', 10);
        this.activateIndex(index, true);
        this.stopAutoplay();
      }
    }

    activateIndex(index, shouldScroll = true) {
      this.currentIndex = index;
      this.updateActiveStates(index, shouldScroll);
    }

    updateActiveStates(index, shouldScroll = true) {
      // Update Cards
      this.cards.forEach((card, i) => {
        const isCurrent = i === index;
        card.classList.toggle('is-active', isCurrent);
        card.setAttribute('aria-expanded', isCurrent ? 'true' : 'false');
      });

      // Update Nav Tabs
      this.navTabs.forEach((tab, i) => {
        const isCurrent = i === index;
        tab.classList.toggle('is-active', isCurrent);
        tab.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      });

      // Handle Scrolling on Mobile
      if (shouldScroll) {
        const targetCard = this.cards[index];
        if (targetCard && this.grid) {
          if (window.innerWidth <= 767) {
            const scrollPos = targetCard.offsetLeft - 16;
            this.grid.scrollTo({
              left: scrollPos,
              behavior: 'smooth'
            });
          }
        }
      }

      // Scroll mobile nav tab into center view
      const targetTab = this.navTabs[index];
      if (targetTab && this.navTrack) {
        const trackWidth = this.navTrack.clientWidth;
        const tabLeft = targetTab.offsetLeft;
        const tabWidth = targetTab.clientWidth;
        const scrollPos = tabLeft - (trackWidth / 2) + (tabWidth / 2);
        this.navTrack.scrollTo({
          left: scrollPos,
          behavior: 'smooth'
        });
      }
    }
  }

  customElements.define('dance-nine-colours', DanceNineColours);
}
